import { useState, useMemo, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import styles from "./styles.module.css";

interface ManpowerCostOverwriteConfirmationProps {
  parsedContent?: any;
  messageId?: string;
}

export default function ManpowerCostOverwriteConfirmation({
  parsedContent,
  messageId,
}: ManpowerCostOverwriteConfirmationProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const agentLoading = useSelector((state: RootState) => state.user.loading);
  const currentChat = useSelector((state: RootState) => state.user.currentChat);

  const [selectedAction, setSelectedAction] = useState<string | null>(null);

  // Session ID
  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("sessionId");
  }, []);

  // Check if this is the latest message
  const isLatestMessage = useMemo(() => {
    if (!messageId) return true;
    const lastAgentMessage = [...currentChat]
      .reverse()
      .find((msg) => msg.sender === "agent");
    return lastAgentMessage?.id === messageId;
  }, [currentChat, messageId]);

  // Handle Overwrite confirmation
  const handleOverwrite = useCallback(() => {
    if (!currentAgent || selectedAction) return;

    setSelectedAction("overwrite");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent.id,
        message: "Yes, overwrite",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent.endpoint,
        sessionId,
      }),
    );
  }, [currentAgent, sessionId, dispatch, selectedAction]);

  // Handle Cancel action
  const handleCancel = useCallback(() => {
    if (!currentAgent || selectedAction) return;

    setSelectedAction("cancel");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent.id,
        message: "No, cancel",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: currentAgent.endpoint,
        sessionId,
      }),
    );
  }, [currentAgent, sessionId, dispatch, selectedAction]);

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Box className={styles.headerIcon}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#00A3A3">
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Box>
        <Typography variant="subtitle2" className={styles.headerText}>
          MANPOWER COST CALCULATOR
        </Typography>
      </Box>

      {/* Warning Message */}
      <Typography className={styles.warningMessage}>
        Are you sure you want to overwrite? The previous data will be lost.
      </Typography>

      {/* Action Buttons */}
      {isLatestMessage && (
        <Box className={styles.buttonContainer}>
          <Button
            variant="contained"
            onClick={handleOverwrite}
            disabled={agentLoading || selectedAction !== null}
            className={styles.confirmButton}
          >
            Yes, Overwrite
          </Button>
          <Button
            variant="text"
            onClick={handleCancel}
            disabled={agentLoading || selectedAction !== null}
            className={styles.cancelButton}
          >
            No, Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
}
